/**
 * FileUploader - Drag & drop file upload with Drive integration
 *
 * Uploads files to Google Drive project folder
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  Cloud,
  X,
  CheckCircle,
  AlertCircle,
  Loader,
  FileText,
} from 'lucide-react';
import { useTranslation } from '../../hooks';

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    overflow: 'hidden',
    boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e5e5',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '16px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#666',
    borderRadius: '4px',
  },
  body: {
    padding: '20px',
  },
  dropzone: {
    border: '2px dashed #ddd',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  },
  dropzoneActive: {
    borderColor: '#2563eb',
    background: '#eff6ff',
  },
  dropzoneText: {
    color: '#666',
    marginTop: '12px',
    fontSize: '14px',
  },
  dropzoneHint: {
    color: '#999',
    fontSize: '12px',
    marginTop: '8px',
  },
  fileList: {
    marginTop: '20px',
    maxHeight: '200px',
    overflowY: 'auto' as const,
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    background: '#f9f9f9',
    borderRadius: '6px',
    marginBottom: '8px',
  },
  fileIcon: {
    width: '36px',
    height: '36px',
    background: '#e5e5e5',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  fileInfo: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: '13px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  fileSize: {
    fontSize: '11px',
    color: '#888',
    marginTop: '2px',
  },
  fileStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#999',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #e5e5e5',
    background: '#fafafa',
  },
  btn: {
    padding: '10px 20px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  btnPrimary: {
    background: '#2563eb',
    color: '#fff',
  },
  btnSecondary: {
    background: '#e5e5e5',
    color: '#333',
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  driveInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: '#e8f4ea',
    borderRadius: '6px',
    marginBottom: '16px',
    fontSize: '13px',
    color: '#137333',
  },
};

// =============================================================================
// TYPES
// =============================================================================

interface FileUploadItem {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

export interface FileUploaderProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<boolean>;
  isConnectedToDrive: boolean;
  projectTitle: string;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// =============================================================================
// COMPONENT
// =============================================================================

export function FileUploader({
  isOpen,
  onClose,
  onUpload,
  isConnectedToDrive,
  projectTitle,
}: FileUploaderProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  }, []);

  const addFiles = (newFiles: File[]) => {
    const items: FileUploadItem[] = newFiles.map((file) => ({
      file,
      id: generateId(),
      status: 'pending',
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...items]);
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpload = async () => {
    if (files.length === 0 || isUploading) return;

    setIsUploading(true);

    for (const item of files) {
      if (item.status === 'success') continue;

      // Update status to uploading
      setFiles((prev) =>
        prev.map((f) => (f.id === item.id ? { ...f, status: 'uploading' } : f))
      );

      try {
        const success = await onUpload(item.file);

        // Update status based on result
        setFiles((prev) =>
          prev.map((f) =>
            f.id === item.id
              ? { ...f, status: success ? 'success' : 'error', progress: 100 }
              : f
          )
        );
      } catch (error) {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === item.id
              ? {
                  ...f,
                  status: 'error',
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : f
          )
        );
      }
    }

    setIsUploading(false);
  };

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      onClose();
    }
  };

  const allDone = files.length > 0 && files.every((f) => f.status === 'success');
  const hasPendingFiles = files.some((f) => f.status === 'pending' || f.status === 'error');

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <Upload size={18} />
            {t('files.uploadFiles')}
          </div>
          <button onClick={handleClose} style={styles.closeBtn} disabled={isUploading}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Drive connection info */}
          {isConnectedToDrive && (
            <div style={styles.driveInfo}>
              <Cloud size={16} />
              {t('files.filesWillBeUploadedTo', { folder: projectTitle })}
            </div>
          )}

          {!isConnectedToDrive && (
            <div
              style={{ ...styles.driveInfo, background: '#fff3e0', color: '#e65100' }}
            >
              <AlertCircle size={16} />
              {t('files.connectDriveInSettings')}
            </div>
          )}

          {/* Dropzone */}
          <div
            style={{
              ...styles.dropzone,
              ...(isDragOver ? styles.dropzoneActive : {}),
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={32} color={isDragOver ? '#2563eb' : '#999'} />
            <p style={styles.dropzoneText} dangerouslySetInnerHTML={{ __html: t('files.dragOrClick') }} />
            <p style={styles.dropzoneHint}>
              {t('files.fileTypes')}
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* File list */}
          {files.length > 0 && (
            <div style={styles.fileList}>
              {files.map((item) => (
                <div key={item.id} style={styles.fileItem}>
                  <div style={styles.fileIcon}>
                    <FileText size={18} color="#666" />
                  </div>
                  <div style={styles.fileInfo}>
                    <div style={styles.fileName}>{item.file.name}</div>
                    <div style={styles.fileSize}>{formatFileSize(item.file.size)}</div>
                  </div>
                  <div style={styles.fileStatus}>
                    {item.status === 'pending' && (
                      <span style={{ color: '#666' }}>{t('files.status.pending')}</span>
                    )}
                    {item.status === 'uploading' && (
                      <>
                        <Loader
                          size={14}
                          color="#2563eb"
                          style={{ animation: 'spin 1s linear infinite' }}
                        />
                        <span style={{ color: '#2563eb' }}>{t('files.status.uploading')}</span>
                      </>
                    )}
                    {item.status === 'success' && (
                      <>
                        <CheckCircle size={14} color="#16a34a" />
                        <span style={{ color: '#16a34a' }}>{t('files.status.completed')}</span>
                      </>
                    )}
                    {item.status === 'error' && (
                      <>
                        <AlertCircle size={14} color="#dc2626" />
                        <span style={{ color: '#dc2626' }}>{t('common.error')}</span>
                      </>
                    )}
                  </div>
                  {item.status !== 'uploading' && item.status !== 'success' && (
                    <button
                      style={styles.removeBtn}
                      onClick={() => removeFile(item.id)}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={handleClose}
            disabled={isUploading}
          >
            {allDone ? t('common.close') : t('common.cancel')}
          </button>
          {!allDone && (
            <button
              style={{
                ...styles.btn,
                ...styles.btnPrimary,
                ...(!hasPendingFiles || !isConnectedToDrive ? styles.btnDisabled : {}),
              }}
              onClick={handleUpload}
              disabled={!hasPendingFiles || isUploading || !isConnectedToDrive}
            >
              {isUploading ? (
                <>
                  <Loader
                    size={16}
                    style={{ animation: 'spin 1s linear infinite' }}
                  />
                  {t('files.status.uploading')}
                </>
              ) : (
                <>
                  <Cloud size={16} />
                  {t('files.uploadToDrive')}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
